import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, CardContent, Typography, FormControl, Select, MenuItem, Stack,
  CircularProgress, Alert, Paper, Backdrop,
} from '@mui/material';
import { AccountBalance as BudgetIcon, CalendarMonth as CalendarIcon, KeyboardArrowDown as ArrowIcon } from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material/Select';
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { CenterSelector } from '../../common/components';
import { useCenterSelection } from '../../common/hooks/useCenterSelection';
import { useGetBudgetSummaryQuery } from '../../../services/LaborManagementService';
import { BudgetVsActualsGrid } from '../components/budget';
import { MonthlyComparisonView } from '../../../services/LaborManagementService/types/laborManagementTypes';

// ── Year Options ────────────────────────────────────────────────────

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - i);

// ── KPI Card ────────────────────────────────────────────────────────

interface KpiCardProps {
  label: string;
  budget: string;
  actual: string;
  variancePercent: number | null;
  accentColor: string;
  favorDirection: 'higher' | 'lower';
}

const KpiCard: React.FC<KpiCardProps> = ({ label, budget, actual, variancePercent, accentColor, favorDirection }) => {
  const variance = variancePercent != null ? `${variancePercent >= 0 ? '+' : ''}${variancePercent.toFixed(1)}%` : '—';
  const isOver = variancePercent != null && variancePercent > 0;
  const isUnder = variancePercent != null && variancePercent < 0;
  const isFavorable = (isOver && favorDirection === 'higher') || (isUnder && favorDirection === 'lower');
  const isUnfavorable = (isOver && favorDirection === 'lower') || (isUnder && favorDirection === 'higher');
  const varianceColor = isFavorable ? '#16a34a' : isUnfavorable ? '#dc2626' : '#6b7280';

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        p: 2,
        borderTop: `3px solid ${accentColor}`,
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderTopColor: accentColor,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, gap: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Budget</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>{budget}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Actual</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>{actual}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Variance</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: varianceColor, lineHeight: 1.2 }}>{variance}</Typography>
        </Box>
      </Box>
    </Paper>
  );
};

// ── Formatters ──────────────────────────────────────────────────────

const fmtCurrency = (v: number) => v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const fmtNumber = (v: number) => v.toLocaleString('en-US', { maximumFractionDigits: 0 });
const fmtDecimal = (v: number) => v.toFixed(1);

// ── Variance Helper ─────────────────────────────────────────────────

const pctVariance = (actual: number, budget: number): number | null => {
  if (budget === 0 && actual === 0) return 0;
  if (budget === 0) return null;
  return ((actual - budget) / budget) * 100;
};

// ── Main Page ───────────────────────────────────────────────────────

const LaborBudget: React.FC = () => {
  const [searchParams] = useSearchParams();
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const { handleCenterChange } = useCenterSelection();

  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  const skip = !company || !center;

  const { data, isLoading, isFetching, isError, error } = useGetBudgetSummaryQuery(
    { company, centerId: center, budgetYear: selectedYear, historicalYear: selectedYear },
    { skip },
  );

  const comparison = data?.historicalComparison;

  const now = new Date();
  const lastCompletedMonth = useMemo(() => {
    if (selectedYear < now.getFullYear()) return 12;
    if (selectedYear === now.getFullYear()) return now.getMonth(); // 0-indexed = previous completed month count
    return 0;
  }, [selectedYear]);

  const completedMonths = useMemo(() => {
    if (!comparison?.months) return [];
    return comparison.months.filter((m: MonthlyComparisonView) => m.month <= lastCompletedMonth);
  }, [comparison?.months, lastCompletedMonth]);

  const computedKpis = useMemo(() => {
    if (completedMonths.length === 0) return null;
    const sumBudgetCases = completedMonths.reduce((s, m) => s + m.budgetCases, 0);
    const sumActualCases = completedMonths.reduce((s, m) => s + m.actualCases, 0);
    const sumBudgetHours = completedMonths.reduce((s, m) => s + m.budgetDirectHours, 0);
    const sumActualHours = completedMonths.reduce((s, m) => s + m.actualDirectHours, 0);
    const sumBudgetCost = completedMonths.reduce((s, m) => s + m.budgetDirectLaborCost, 0);
    const sumActualCost = completedMonths.reduce((s, m) => s + m.actualDirectLaborCost, 0);
    const budgetHpc = sumBudgetCases > 0 ? sumBudgetHours / sumBudgetCases : 0;
    const actualHpc = sumActualCases > 0 ? sumActualHours / sumActualCases : 0;
    return {
      sumBudgetCases, sumActualCases, casesVar: pctVariance(sumActualCases, sumBudgetCases),
      sumBudgetHours, sumActualHours, hoursVar: pctVariance(sumActualHours, sumBudgetHours),
      sumBudgetCost, sumActualCost, costVar: pctVariance(sumActualCost, sumBudgetCost),
      budgetHpc, actualHpc, hpcVar: pctVariance(actualHpc, budgetHpc),
    };
  }, [completedMonths]);

  const handleYearChange = (event: SelectChangeEvent<number>) => {
    setSelectedYear(Number(event.target.value));
  };

  const hasData = comparison?.hasBudgetData || comparison?.hasHistoricalData;

  return (
    <PageContainer>
      <Backdrop open={isFetching} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, flexDirection: 'column', gap: 2 }}>
        <CircularProgress color="inherit" size={56} />
        <Typography variant="h6">Loading budget data…</Typography>
      </Backdrop>

      <Box display="flex" justifyContent="center">
        <PageCard>
          {/* Header */}
          <Box sx={{
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #3b82f6',
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <BudgetIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Budget vs Actuals
              </Typography>
            </Box>
          </Box>

          {/* Content */}
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <CenterSelector value={center} onChange={handleCenterChange} />
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  borderRadius: 3,
                  px: 2,
                  py: 0.75,
                  border: '2px solid #cbd5e1',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  transition: 'all 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: '#94a3b8',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: '#64748b',
                    flexShrink: 0,
                  }}
                >
                  <CalendarIcon sx={{ color: '#ffffff', fontSize: 18 }} />
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 1,
                      fontSize: '0.65rem',
                      lineHeight: 1,
                      display: 'block',
                    }}
                  >
                    Year
                  </Typography>
                  <Select<number>
                    size="small"
                    value={selectedYear}
                    onChange={handleYearChange}
                    variant="standard"
                    disableUnderline
                    IconComponent={ArrowIcon}
                    sx={{
                      '& .MuiSelect-select': { py: 0, pr: '28px !important', fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' },
                      '& .MuiSvgIcon-root': { color: '#64748b', fontSize: 22 },
                      '& .MuiSelect-select:focus': { backgroundColor: 'transparent' },
                    }}
                  >
                    {YEAR_OPTIONS.map((year) => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </Select>
                </Box>
              </Stack>
            </Box>

            {!company || !center ? (
              <Alert severity="info">Select a center to view budget vs actual data.</Alert>
            ) : isError ? (
              <Alert severity="error">
                Failed to load budget data. {(error as any)?.data?.message || 'Please try again.'}
              </Alert>
            ) : !hasData ? (
              <Alert severity="info">
                No budget or actual data available for {selectedYear}. Try selecting a different year.
              </Alert>
            ) : (
              <>
                {computedKpis && (
                  <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                    <KpiCard label="Cases" budget={fmtNumber(computedKpis.sumBudgetCases)} actual={fmtNumber(computedKpis.sumActualCases)} variancePercent={computedKpis.casesVar} accentColor="#3b82f6" favorDirection="higher" />
                    <KpiCard label="Direct Hours" budget={fmtNumber(computedKpis.sumBudgetHours)} actual={fmtNumber(computedKpis.sumActualHours)} variancePercent={computedKpis.hoursVar} accentColor="#16a34a" favorDirection="lower" />
                    <KpiCard label="Labor Cost" budget={fmtCurrency(computedKpis.sumBudgetCost)} actual={fmtCurrency(computedKpis.sumActualCost)} variancePercent={computedKpis.costVar} accentColor="#f59e0b" favorDirection="lower" />
                    <KpiCard label="Hours / Case" budget={fmtDecimal(computedKpis.budgetHpc)} actual={fmtDecimal(computedKpis.actualHpc)} variancePercent={computedKpis.hpcVar} accentColor="#8b5cf6" favorDirection="lower" />
                  </Box>
                )}
                <BudgetVsActualsGrid months={comparison!.months} lastCompletedMonth={lastCompletedMonth} />
              </>
            )}
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default LaborBudget;
